import { Button } from '@design-system';
import { useTranslation } from 'next-i18next';
import React, { FC, useCallback, useMemo } from 'react';

import { generalSubscriptionsInput } from '../../constants/subscriptions';
import { AuthEntryPoint } from '../../features/auth/types';
import { useIsSubscribed } from '../../hooks/subscription';
import { useAuthenticate } from '../../hooks/useAuthenticate';
import { GeneratedCreateSubscriptionsInput } from '../../types/generated';
import { ButtonLocation } from '../../types/Subscriptions';
import { ShareButtons } from '../Share/ShareButtons';

interface HeroButtonsProps {
  buttonLocation: ButtonLocation;
  handleShare: (platform: string) => void;
  title: string;
}

export const HeroButtons: FC<HeroButtonsProps> = ({
  buttonLocation,
  handleShare,
  title,
}) => {
  const { t } = useTranslation();
  const { authenticateAndSubscribe } = useAuthenticate();

  const subscriptions: GeneratedCreateSubscriptionsInput[] = useMemo(
    () => [generalSubscriptionsInput],
    [],
  );

  const { isSubscribedAll, loading, refetch } = useIsSubscribed(
    subscriptions.map(({ objectId, type }) => ({ objectId, type })),
  );

  const handleSubmit = useCallback(() => {
    const handleSubscribe = async () => {
      if (isSubscribedAll) {
        return;
      }

      await authenticateAndSubscribe({
        subscriptions,
        analyticsProperties: {
          button_location: buttonLocation,
          source: AuthEntryPoint.NewsletterInsights,
        },
      });
      refetch();
    };

    handleSubscribe();
  }, [
    authenticateAndSubscribe,
    buttonLocation,
    isSubscribedAll,
    refetch,
    subscriptions,
  ]);

  return (
    <div className="flex items-center justify-center gap-3">
      <ShareButtons onClick={handleShare} text={title} />
      <Button
        disabled={loading}
        icon={
          isSubscribedAll ? 'ic_notification_active' : 'ic_notification_outline'
        }
        label={isSubscribedAll ? t('subscribed') : t('subscribe')}
        loading={loading}
        onClick={handleSubmit}
        size="sm"
        variant={isSubscribedAll ? 'positive' : 'tertiary'}
      />
    </div>
  );
};
